// Shared logic for matching MonthlyCompensation rows to real NetSuite Check
// transactions. Used by admin/Check.jsx (the check-creation workflow) and by
// the Cash Flow calendar (which overlays real check data on the Associate
// Fee outflow row instead of a generic scheduled amount).
import { calculateDdsDraw } from "./associateFee";

// Only DDS rows, plus HYG rows that have an associate-fee percentage set,
// are eligible for an associate-fee check.
export function filterAssociateFeeEligibleRows(rows) {
  return (rows ?? []).filter((r) => {
    const type = (r.practitioner_type || "").toUpperCase();
    if (type === "DDS") return true;
    if (type === "HYG") {
      const pct = Number(r.associate_fee_percentage);
      return Number.isFinite(pct) && pct > 0;
    }
    return false;
  });
}

// Clinic code a check's memo names ("Associate Fee - September 2026 - ON-T-BRO
// - Smiles Analytics" → "ON-T-BRO"), or null for older memos that name none.
const MEMO_CLINIC_RE = /\b[A-Z]{2}-[A-Z]-[A-Z]{3}\b/;
function clinicFromMemo(memo) {
  const m = MEMO_CLINIC_RE.exec(String(memo ?? "").toUpperCase());
  return m ? m[0] : null;
}

// A check whose memo names a clinic belongs to that clinic's row only. A
// practitioner working at several clinics gets one check per clinic, and
// matching by vendor alone let another clinic's check land on this one.
function checkFitsRow(check, row) {
  return !check.clinic || check.clinic === String(row?.clinic ?? "").toUpperCase();
}

// Groups a raw NetSuite check search response by vendor id.
export function buildChecksByVendor(rawList) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const map = {};
  for (const item of Array.isArray(rawList) ? rawList : []) {
    const vid = item.entity_id ?? item.entity ?? item.entityid;
    if (vid === null || vid === undefined || vid === "") continue;
    const amount = round2(item.amount ?? item.grosstotal ?? 0);
    (map[String(vid)] ||= []).push({
      // Carried so ClinicCashFlow can tell a matched check apart from the same
      // check in its outstanding pass — without it every match double-counts.
      id: item.id ?? null,
      trandate: item.trandate,
      transactionnumber: item.transactionnumber ?? item.tranid,
      link: item.link ?? null,
      amount,
      cleared: item.cleared === true || item.cleared === "T" || item.cleared === "t",
      cleareddate: item.cleareddate ?? item.clearedDate ?? null,
      clinic: clinicFromMemo(item.memo),
    });
  }
  return map;
}

// Expected clear date per vendor, based on historical cleared checks: the
// average day-of-month of the vendor's most recent cleared dates.
export function computeExpectedClearInfoByVendor(clearedDatesData, parseMonthDayFromDateString) {
  const groups = clearedDatesData?.items || {};
  const map = {};

  for (const [vendorId, group] of Object.entries(groups)) {
    const list = Array.isArray(group?.items) ? group.items : [];
    const clearedDates = list.map((item) => String(item?.cleareddate || "").trim()).filter(Boolean);
    const days = list
      .map((item) => parseMonthDayFromDateString(item?.cleareddate || "")?.dayNum)
      .filter((dayNum) => Number.isFinite(dayNum));

    if (!days.length) continue;

    const avgDay = Math.round(days.reduce((sum, d) => sum + d, 0) / days.length);
    const clampedDay = Math.max(1, Math.min(31, avgDay));
    map[String(vendorId)] = {
      day: clampedDay,
      label: `Expected clear: day ${clampedDay}`,
      clearedDates,
    };
  }

  return map;
}

// Finds the smallest-cardinality subset of `pool` whose amounts sum to
// `target` (within a cent). Returns the array of indices into `pool`, or
// null if no subset matches. Bitmask enumeration — safe because a vendor
// rarely has more than a handful of checks per month.
function findSubset(pool, target) {
  const n = pool.length;
  if (n === 0 || target <= 0) return null;
  if (n > 12) return null; // cap search space to avoid pathological cases
  const tgtCents = Math.round(target * 100);
  let best = null;
  const total = 1 << n;
  for (let mask = 1; mask < total; mask++) {
    let sum = 0;
    let count = 0;
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        sum += Math.round(pool[i].amount * 100);
        count += 1;
      }
    }
    if (sum === tgtCents) {
      if (best === null || count < best.count) {
        const idx = [];
        for (let i = 0; i < n; i++) if (mask & (1 << i)) idx.push(i);
        best = { count, idx };
        if (count === 1) break; // best possible
      }
    }
  }
  return best ? best.idx : null;
}

// Maps each row to the checks that belong to it.
//
// A practitioner (vendor) may appear on multiple rows (one per clinic). A
// practitioner may also have multiple NetSuite checks per row whose
// grosstotal sums to that row's expected amount. So per vendor we try, in
// row order, to find a subset of the vendor's remaining checks whose sum
// equals the row's expected amount. The smallest matching subset wins
// (prefer single-check exact matches). Any vendor checks not consumed by
// any row are appended to that vendor's first row so they remain visible.
export function matchChecksToRows(
  rows,
  checksByVendor,
  { getExpectedAmount = calculateDdsDraw } = {},
) {
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const map = new Map();
  const remaining = {};
  const rowIdsByVendor = {};
  for (const vid of Object.keys(checksByVendor)) {
    remaining[vid] = [...checksByVendor[vid]];
  }
  for (const r of rows) {
    map.set(r.id, []);
    const vid = r?.netsuite_vendor_id;
    if (vid === null || vid === undefined || vid === "") continue;
    const key = String(vid);
    (rowIdsByVendor[key] ||= []).push(r);
    const pool = remaining[key];
    if (!pool || pool.length === 0) continue;
    const expected = round2(Number(getExpectedAmount(r)) || 0);
    if (expected <= 0) continue;
    // Search only the checks this row may take; map picks back to pool indices.
    const eligible = pool.map((c, i) => i).filter((i) => checkFitsRow(pool[i], r));
    const picks = findSubset(
      eligible.map((i) => pool[i]),
      expected,
    );
    if (!picks) continue;
    const taken = [];
    for (const i of picks.map((j) => eligible[j]).sort((a, b) => b - a)) {
      taken.push(...pool.splice(i, 1));
    }
    taken.sort((a, b) => String(a.trandate).localeCompare(String(b.trandate)));
    map.set(r.id, taken);
  }
  // Distribute leftover checks (amount didn't match any row) to the
  // vendor's rows that still have no checks, one leftover per empty row; any
  // still left go to the first row they fit. A check that fits none of the
  // rows shown (another clinic's, with that clinic not loaded) is dropped.
  for (const [vid, pool] of Object.entries(remaining)) {
    if (!pool.length) continue;
    const vendorRows = rowIdsByVendor[vid] || [];
    if (vendorRows.length === 0) continue;
    pool.sort((a, b) => String(a.trandate).localeCompare(String(b.trandate)));
    for (const check of pool) {
      const fits = vendorRows.filter((r) => checkFitsRow(check, r));
      if (fits.length === 0) continue;
      const target = fits.find((r) => (map.get(r.id) || []).length === 0) ?? fits[0];
      map.set(target.id, (map.get(target.id) || []).concat(check));
    }
  }
  return map;
}
